import {
  CustomerPortalInvoice,
  CustomerPortalSessionData,
} from '@monetize/types/app';
import { getErrorMessage } from '@monetize/utils/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  MdOutlineBusinessCenter,
  MdOutlineCreditCard,
  MdOutlineDashboard,
  MdOutlineReceiptLong,
} from 'react-icons/md';
import {
  useGetCustomerPortalBillGroupData,
  useGetCustomerPortalCompanyInfo,
  useGetCustomerPortalInvoicesInfiniteLoad,
  useGetCustomerPortalPaymentConfiguration,
  useGetCustomerPortalPaymentMethodsInfiniteLoad,
  useGetCustomerPortalSubscriptionView,
} from '../../../api/customerPortalService';
import AppLoading from '../../../components/App/AppLoading';
import {
  MBox,
  MFlex,
  MIcon,
  MTab,
  MTabList,
  MTabPanel,
  MTabPanels,
  MTabs,
  MText,
} from '../../../components/Monetize';
import MButton from '../../../components/Monetize/MButton';
import { SubscriptionViewCard } from '../../../components/SubscriptionView';
import Sentry from '../../../services/sentry';
import { BillingDetailsCard } from './components/BillingDetailsCard';
import { BillingSummaryCard } from './components/BillingSummaryCard';
import { ContactsCard } from './components/contacts/ContactsCard';
import { ContractTermBanner } from './components/ContractTermBanner';
import { CustomerPortalFooter } from './components/CustomerPortalFooter';
import { CustomerPortalHeader } from './components/CustomerPortalHeader';
import { InvoicesCard } from './components/InvoicesCard';
import { PayInvoiceModal } from './components/PayInvoiceModal';
import { PaymentMethodsCard } from './components/paymentMethods/PaymentMethodsCard';
import { ViewInvoiceModal } from './components/ViewInvoiceModal';
import {
  CUSTOMER_PORTAL_TABS,
  CustomerPortalTabs,
} from './customerPortalUtils';

interface CustomerPortalPageProps extends CustomerPortalSessionData {
  /** Omitted when logout isn't supported (e.g. preview mode). */
  onLogout?: () => void;
}

export const CustomerPortalPage = ({
  accountId,
  billGroupId,
  sessionId,
  tenantId,
  accessToken,
  email,
  onLogout,
}: CustomerPortalPageProps) => {
  const tabRefs = useRef<{
    [key in CustomerPortalTabs]: HTMLButtonElement | null;
  }>({
    [CUSTOMER_PORTAL_TABS.OVERVIEW]: null,
    [CUSTOMER_PORTAL_TABS.INVOICES]: null,
    [CUSTOMER_PORTAL_TABS.BILLING_DETAILS]: null,
    [CUSTOMER_PORTAL_TABS.PAYMENT_METHODS]: null,
  });

  const [viewInvoice, setViewInvoice] = useState<CustomerPortalInvoice | null>(
    null,
  );
  const [invoiceToPay, setInvoiceToPay] =
    useState<CustomerPortalInvoice | null>(null);

  const {
    data: billGroupData,
    isLoading: billGroupLoading,
    isError: billGroupError,
    error: billGroupErrorDetails,
  } = useGetCustomerPortalBillGroupData({
    accountId,
    billGroupId,
    sessionId,
    tenantId,
    accessToken,
    email,
  });

  const {
    data: companyInfoData,
    isLoading: companyLogoLoading,
    error: companyInfoErrorDetails,
  } = useGetCustomerPortalCompanyInfo(
    {
      accountId,
      billGroupId,
      sessionId,
      tenantId,
      accessToken,
      email,
    },
    { retry: false },
  );

  const {
    data: invoicesData,
    isLoading: invoicesLoading,
    isError: invoicesError,
    hasNextPage: invoicesHasNextPage,
    fetchNextPage: invoicesFetchNextPage,
    isFetchingNextPage: invoicesIsFetchingNextPage,
    error: invoicesErrorDetails,
  } = useGetCustomerPortalInvoicesInfiniteLoad({
    accountId,
    billGroupId,
    sessionId,
    tenantId,
    accessToken,
    email,
  });

  const {
    data: paymentMethodsData,
    isLoading: paymentMethodsLoading,
    isError: paymentMethodsError,
    hasNextPage: paymentMethodsHasNextPage,
    fetchNextPage: paymentMethodsFetchNextPage,
    isFetchingNextPage: paymentMethodsIsFetchingNextPage,
    error: paymentMethodsErrorDetails,
  } = useGetCustomerPortalPaymentMethodsInfiniteLoad({
    accountId,
    billGroupId,
    sessionId,
    tenantId,
    accessToken,
    email,
  });

  const {
    data: subscriptionViewData,
    isLoading: subscriptionsLoading,
    isError: subscriptionsError,
    error: subscriptionsErrorDetails,
  } = useGetCustomerPortalSubscriptionView({
    accountId,
    billGroupId,
    sessionId,
    tenantId,
    accessToken,
    email,
  });

  const { data: paymentConfiguration } =
    useGetCustomerPortalPaymentConfiguration({
      accountId,
      billGroupId,
      sessionId,
      tenantId,
      accessToken,
      email,
    });

  const isLoading =
    billGroupLoading ||
    companyLogoLoading ||
    invoicesLoading ||
    paymentMethodsLoading ||
    subscriptionsLoading;

  useEffect(() => {
    if (
      billGroupErrorDetails ||
      companyInfoErrorDetails ||
      invoicesErrorDetails ||
      paymentMethodsErrorDetails ||
      subscriptionsErrorDetails
    ) {
      Sentry.logger.error('CustomerPortalPage - data load failed', {
        billGroupError: billGroupErrorDetails
          ? getErrorMessage(billGroupErrorDetails)
          : undefined,
        companyInfoError: companyInfoErrorDetails
          ? getErrorMessage(companyInfoErrorDetails)
          : undefined,
        invoicesError: invoicesErrorDetails
          ? getErrorMessage(invoicesErrorDetails)
          : undefined,
        paymentMethodsError: paymentMethodsErrorDetails
          ? getErrorMessage(paymentMethodsErrorDetails)
          : undefined,
        subscriptionsError: subscriptionsErrorDetails
          ? getErrorMessage(subscriptionsErrorDetails)
          : undefined,
      });
    }
  }, [
    accountId,
    billGroupId,
    billGroupErrorDetails,
    companyInfoErrorDetails,
    invoicesErrorDetails,
    paymentMethodsErrorDetails,
    subscriptionsErrorDetails,
  ]);

  const sessionData: CustomerPortalSessionData = useMemo(
    () => ({
      accessToken,
      accountId,
      billGroupId,
      sessionId,
      tenantId,
      email,
    }),
    [accessToken, accountId, billGroupId, sessionId, tenantId, email],
  );

  const handleInvoiceView = (invoice: CustomerPortalInvoice) => {
    Sentry.logger.info('CustomerPortalPage - invoice view', {
      invoiceId: invoice.id,
      tenantId,
    });
    setViewInvoice(invoice);
  };

  useEffect(() => {
    if (!isLoading && billGroupData) {
      Sentry.logger.info('CustomerPortalPage accessed', {
        accountId,
        billGroupId,
      });
    }
  }, [accountId, billGroupData, billGroupId, isLoading]);

  if (billGroupError) {
    return (
      <MBox minH="100vh" display="flex" flexDirection="column" bg="white">
        <MFlex
          flex={1}
          alignItems="center"
          justifyContent="center"
          px={4}
          py={8}
        >
          <MBox
            bg="white"
            borderRadius="lg"
            p={8}
            maxW="400px"
            w="100%"
            textAlign="center"
            boxShadow="md"
          >
            <MText fontSize="xl" fontWeight="600" color="fg.strong" mb={2}>
              Unable to Load Data
            </MText>
            <MText color="fg.muted" fontSize="sm" mb={6}>
              We couldn't load your information. Your session may have expired.
            </MText>
            {onLogout && (
              <MButton onClick={onLogout} w="100%">
                Sign in again
              </MButton>
            )}
          </MBox>
        </MFlex>
        <CustomerPortalFooter />
      </MBox>
    );
  }

  if (isLoading || !billGroupData) {
    return <AppLoading />;
  }

  const { billGroup, account, permissions, supportConfiguration } =
    billGroupData;
  const selectedPaymentGatewayId = billGroup.defaultPaymentGatewayId;
  const {
    billingContact,
    shippingContact,
    addressSource,
    billingAddress,
    shippingAddress,
    latestInServiceContract,
  } = billGroup;

  const tabVisibility = {
    overview: permissions.subscriptions.view,
    invoices: permissions.invoices.view,
    billingDetails:
      permissions.billingSummary.view || permissions.contacts.view,
    paymentMethods: permissions.paymentMethods.view,
  };
  const visibleTabCount =
    Number(tabVisibility.overview) +
    Number(tabVisibility.invoices) +
    Number(tabVisibility.billingDetails) +
    Number(tabVisibility.paymentMethods);
  const showTabList = visibleTabCount > 1;

  return (
    <MBox bg="white" minH="100vh" display="flex" flexDirection="column">
      <CustomerPortalHeader
        accountName={account.name}
        companyName={companyInfoData?.companyName}
        companyLogo={companyInfoData?.companyLogo}
        email={email}
        onLogout={onLogout}
        showLogout={!!onLogout}
      />

      <MFlex
        flex={1}
        width="100%"
        maxW="80rem"
        mx="auto"
        flexDirection="column"
        px={{ base: 4, md: 8 }}
        py={6}
        gap={4}
      >
        {latestInServiceContract?.term && (
          <ContractTermBanner
            status={latestInServiceContract.status}
            termStart={latestInServiceContract.term.start}
            termEnd={latestInServiceContract.term.end}
            latestQuoteId={latestInServiceContract.latestQuoteId}
            sessionData={sessionData}
          />
        )}

        {permissions.billingSummary.view && (
          <BillingSummaryCard
            billGroup={billGroup}
            invoices={invoicesData || []}
            onNavigate={(tab) => tabRefs.current[tab]?.click()}
            canNavigateToInvoices={tabVisibility.invoices}
          />
        )}

        <MTabs variant="pill" isLazy>
          {showTabList && (
            <MFlex justifyContent="center" mb={2}>
              <MTabList>
                {tabVisibility.overview && (
                  <MTab
                    ref={(el) => {
                      tabRefs.current[CUSTOMER_PORTAL_TABS.OVERVIEW] = el;
                    }}
                  >
                    <MIcon as={MdOutlineDashboard} boxSize={4} />
                    Overview
                  </MTab>
                )}
                {tabVisibility.invoices && (
                  <MTab
                    ref={(el) => {
                      tabRefs.current[CUSTOMER_PORTAL_TABS.INVOICES] = el;
                    }}
                  >
                    <MIcon as={MdOutlineReceiptLong} boxSize={4} />
                    Invoices
                  </MTab>
                )}
                {tabVisibility.billingDetails && (
                  <MTab
                    ref={(el) => {
                      tabRefs.current[CUSTOMER_PORTAL_TABS.BILLING_DETAILS] =
                        el;
                    }}
                  >
                    <MIcon as={MdOutlineBusinessCenter} boxSize={4} />
                    Billing Details
                  </MTab>
                )}
                {tabVisibility.paymentMethods && (
                  <MTab
                    ref={(el) => {
                      tabRefs.current[CUSTOMER_PORTAL_TABS.PAYMENT_METHODS] =
                        el;
                    }}
                  >
                    <MIcon as={MdOutlineCreditCard} boxSize={4} />
                    Payment Methods
                  </MTab>
                )}
              </MTabList>
            </MFlex>
          )}
          <MTabPanels>
            {tabVisibility.overview && (
              <MTabPanel px={0} py={2}>
                <SubscriptionViewCard
                  data={subscriptionViewData}
                  isLoading={subscriptionsLoading}
                  isError={subscriptionsError}
                />
              </MTabPanel>
            )}

            {tabVisibility.invoices && (
              <MTabPanel px={0} py={2}>
                <InvoicesCard
                  invoices={invoicesData || []}
                  hasLoadError={invoicesError}
                  hasMore={invoicesHasNextPage}
                  isLoadingMore={invoicesIsFetchingNextPage}
                  canPayInvoices={permissions.invoices.pay}
                  onView={handleInvoiceView}
                  onLoadMore={invoicesFetchNextPage}
                  onPay={setInvoiceToPay}
                />
              </MTabPanel>
            )}

            {tabVisibility.billingDetails && (
              <MTabPanel px={0} py={2}>
                <MFlex flexDirection="column" gap={4}>
                  {permissions.billingSummary.view && (
                    <BillingDetailsCard
                      sessionData={sessionData}
                      billGroup={billGroup}
                      permissions={permissions.billingSummary}
                    />
                  )}
                  {permissions.contacts.view && (
                    <ContactsCard
                      addressSource={addressSource}
                      billingAddress={billingAddress}
                      billingContact={billingContact}
                      shippingAddress={shippingAddress}
                      shippingContact={shippingContact}
                      sessionData={sessionData}
                      permissions={permissions.contacts}
                    />
                  )}
                </MFlex>
              </MTabPanel>
            )}

            {tabVisibility.paymentMethods && (
              <MTabPanel px={0} py={2}>
                <PaymentMethodsCard
                  sessionData={sessionData}
                  billGroup={billGroup}
                  defaultPaymentMethodId={billGroup.paymentMethodId}
                  paymentMethods={paymentMethodsData || []}
                  paymentGatewayId={selectedPaymentGatewayId}
                  hasLoadError={paymentMethodsError}
                  hasMore={paymentMethodsHasNextPage}
                  isLoadingMore={paymentMethodsIsFetchingNextPage}
                  onLoadMore={paymentMethodsFetchNextPage}
                  permissions={permissions.paymentMethods}
                />
              </MTabPanel>
            )}
          </MTabPanels>
        </MTabs>
      </MFlex>

      <CustomerPortalFooter supportEmail={supportConfiguration?.supportEmail} />

      {viewInvoice && (
        <ViewInvoiceModal
          invoice={viewInvoice}
          sessionData={sessionData}
          canPayInvoices={permissions.invoices.pay}
          onClose={() => setViewInvoice(null)}
          onPay={(invoice) => {
            setViewInvoice(null);
            setInvoiceToPay(invoice);
          }}
        />
      )}

      {invoiceToPay && (
        <PayInvoiceModal
          isOpen
          onClose={() => setInvoiceToPay(null)}
          invoice={invoiceToPay}
          sessionData={sessionData}
          billGroup={billGroup}
          paymentMethods={paymentMethodsData || []}
          paymentConfiguration={paymentConfiguration}
        />
      )}
    </MBox>
  );
};
